"use client";

import { motion } from "framer-motion";
import {
  Trophy,
  Medal,
  Star,
  TrendingUp,
} from "lucide-react";

import Section from "./ui/Section";
import SectionHeading from "./ui/SectionHeading";
import ProductShowcase from "./showcase/ProductShowcase";

const highlights = [
  {
    icon: Trophy,
    title: "Competition Rankings",
    description:
      "Review your position in competitions when rankings are available for the published test.",
  },
  {
    icon: Medal,
    title: "Institute Leaderboards",
    description:
      "See how your performance compares with other participants in your institute.",
  },
  {
    icon: TrendingUp,
    title: "XP Progression",
    description:
      "Build your learning profile through eligible activities that award experience points.",
  },
  {
    icon: Star,
    title: "Achievements",
    description:
      "Track milestones and unlock achievement badges as you progress.",
  },
];

export default function CompetitionExperience() {
  return (
    <Section className="relative overflow-hidden bg-[#050B1A] py-20 lg:py-28">
      {/* Decorative blue and gold background accents */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-brand-gold/[0.05] blur-3xl" />
        <div className="absolute -right-40 top-1/4 h-[32rem] w-[32rem] rounded-full bg-blue-500/[0.06] blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div className="relative z-10 grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        {/* Competition and progress highlights */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="min-w-0"
        >
          <SectionHeading
            badge="COMPETITION & PROGRESS"
            title="Compete. Improve. Get Recognized."
            subtitle="Make practice more engaging with competition results, XP progression, and milestones that help you track your learning journey."
            dark
          />

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {highlights.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ y: -2 }}
                  className="
                    group
                    flex
                    min-w-0
                    gap-4
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.03]
                    p-4
                    transition-colors
                    duration-300
                    hover:border-brand-gold/25
                    hover:bg-white/[0.05]
                  "
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/[0.07] text-brand-gold transition-colors group-hover:border-brand-gold/45 group-hover:bg-brand-gold/10">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-semibold text-white">{item.title}</h3>
                    <p className="mt-1 text-xs leading-5 text-white/55 sm:text-sm sm:leading-6">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-5 text-xs leading-5 text-white/40">
            Rankings, XP, and achievements depend on the relevant activity,
            competition, and platform rules.
          </p>
        </motion.div>

        {/* Illustrative leaderboard preview */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, delay: 0.08, ease: "easeOut" }}
          className="relative min-w-0"
        >
          <div
            className="pointer-events-none absolute -inset-5 rounded-[2.5rem] bg-brand-gold/[0.045] blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <ProductShowcase mode="leaderboard" />
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
